/**
 * User-friendly name mappings for support team readability.
 */

import type { OperatingSystem, DeviceType, BrowserName } from "./types";

/**
 * Maps technical OS names to user-friendly display names.
 */
export function getFriendlyOSName(os: OperatingSystem): string {
  const friendlyNames: Record<OperatingSystem, string> = {
    mac: "macOS",
    windows: "Windows",
    linux: "Linux",
    android: "Android",
    ios: "iOS",
    unknown: "Unknown Operating System",
  };
  return friendlyNames[os];
}

/**
 * Maps technical device type names to user-friendly display names.
 */
export function getFriendlyDeviceName(device: DeviceType): string {
  const friendlyNames: Record<DeviceType, string> = {
    desktop: "Desktop Computer",
    mobile: "Mobile Phone",
    tablet: "Tablet",
    unknown: "Unknown Device",
  };
  return friendlyNames[device];
}

/**
 * Maps technical browser names to user-friendly display names.
 */
export function getFriendlyBrowserName(browser: BrowserName): string {
  const friendlyNames: Record<BrowserName, string> = {
    chrome: "Google Chrome",
    edge: "Microsoft Edge",
    firefox: "Mozilla Firefox",
    safari: "Apple Safari",
    opera: "Opera Browser",
    brave: "Brave Browser",
    yandex: "Yandex Browser",
    unknown: "Unknown Browser",
  };
  return friendlyNames[browser];
}

/**
 * Creates a user-friendly description for support teams.
 */
export function getSupportDescription(
  os: OperatingSystem,
  device: DeviceType,
  browser: BrowserName,
  browserVersion: string | null
): string {
  const osName = getFriendlyOSName(os);
  const deviceName = getFriendlyDeviceName(device);
  const browserName = getFriendlyBrowserName(browser);
  const versionText = browserVersion ? ` version ${browserVersion}` : "";

  return `${browserName}${versionText} on ${osName} (${deviceName})`;
}
